/**
 * dtrader-c side of the host <-> iframe postMessage handshake.
 *
 *   1. On boot the iframe sends `READY` to its parent.
 *   2. The parent responds with `AUTH` (legacy or oauth2 payload).
 *   3. The iframe hydrates localStorage and authorizes via the existing client-store path.
 *   4. Subsequent `SWITCH` calls trigger `client.switchAccount`.
 *   5. `LOGOUT` clears local state and reloads to the logged-out shell.
 *
 * Embed security: the **direct parent** origin must match `ALLOWED_PARENT_HOST_SUFFIXES` (PU
 * registrable domains) and/or `PU_PARENT_ORIGIN` / `BOTBLITZ_PARENT_ORIGINS` (or legacy `EVERYMAN_PARENT_ORIGINS`). Random sites cannot
 * drive the bridge. Prefer CSP `frame-ancestors` on static hosting as a second layer.
 */

import { BOTBLITZ_DTRADER_SYNC_COOKIE_NAME } from '@deriv/shared';
import Cookies from 'js-cookie';
import BinarySocket from '_common/base/socket_base';
import {
    isProtocolMessage,
    PROTOCOL,
    type TAuthPayload,
    type THostToIframeMessage,
    type TIframeToHostMessage,
} from '@deriv/utils';

const AUTH_VERBOSE = '🖼️ [everyman-auth-verbose]';

/** Redacted account id for logs (prefix + length only; no token material). */
const logAccountHint = (id: string | undefined): { prefix: string; len: number } => ({
    prefix: typeof id === 'string' && id.length >= 2 ? id.slice(0, 3) : '',
    len: typeof id === 'string' ? id.length : 0,
});

/** When root sync cookie exists, AUTH payload must align with `active_loginid` / flow (`v: 2` only). */
const validateSyncCookieAllowsPayload = (payload: TAuthPayload): boolean => {
    try {
        const raw = Cookies.get(BOTBLITZ_DTRADER_SYNC_COOKIE_NAME);
        if (!raw) {
            // eslint-disable-next-line no-console
            console.info(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', {
                outcome: 'no_cookie_skip_validation',
                payload_flow: payload.flow,
                ...logAccountHint(
                    payload.flow === 'oauth2' ? payload.active_account_id : payload.active_loginid
                ),
            });
            return true;
        }
        const sync = JSON.parse(raw) as {
            v?: number;
            auth_flow?: string;
            active_loginid?: string;
            app_id?: string;
            oauth_client_id?: string;
            deriv_app_id?: string;
        };
        /* `v` = sync-cookie format revision (2, or deprecated 3); not “API v3”. */
        if (!sync || (sync.v !== 2 && sync.v !== 3)) {
            // eslint-disable-next-line no-console
            console.warn(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', {
                outcome: 'warn_unexpected_cookie_version',
                cookie_v: sync?.v,
                payload_flow: payload.flow,
            });
            return true;
        }
        const expectedFlow = payload.flow === 'oauth2' ? 'oauth2' : 'legacy';
        if (sync.auth_flow && sync.auth_flow !== expectedFlow) {
            // eslint-disable-next-line no-console
            console.warn(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', {
                outcome: 'reject_auth_flow_mismatch',
                cookie_auth_flow: sync.auth_flow,
                payload_flow: expectedFlow,
            });
            return false;
        }
        const expectedId = payload.flow === 'oauth2' ? payload.active_account_id : payload.active_loginid;
        if (sync.active_loginid && expectedId && sync.active_loginid !== expectedId) {
            // eslint-disable-next-line no-console
            console.warn(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', {
                outcome: 'reject_active_loginid_mismatch',
                cookie_hint: logAccountHint(sync.active_loginid),
                payload_hint: logAccountHint(expectedId),
            });
            return false;
        }
        // eslint-disable-next-line no-console
        console.info(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', {
            outcome: 'ok',
            cookie_format_v: sync.v,
            cookie_auth_flow: sync.auth_flow,
            sync_kind: sync.auth_flow === 'oauth2' ? 'oauth2_new_api' : 'legacy_app_id',
            app_id_len: typeof sync.app_id === 'string' ? sync.app_id.length : 0,
            deriv_app_id_len: typeof sync.deriv_app_id === 'string' ? sync.deriv_app_id.length : 0,
            oauth_client_id_len:
                typeof sync.oauth_client_id === 'string' ? sync.oauth_client_id.trim().length : 0,
            payload_flow: payload.flow,
            payload_hint: logAccountHint(expectedId),
        });
        return true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(AUTH_VERBOSE, 'sync_cookie_vs_AUTH', { outcome: 'parse_error_allow', error: String(e) });
        return true;
    }
};

const STORAGE_KEYS = {
    AUTH_FLOW: 'auth_flow',
    OAUTH2_ACCESS_TOKEN: 'oauth2_access_token',
    OAUTH2_ACCESS_TOKEN_EXPIRES_AT: 'oauth2_access_token_expires_at',
    OAUTH2_ACTIVE_ACCOUNT_ID: 'oauth2_active_account_id',
    ACCOUNTS_LIST: 'accountsList',
    CLIENT_ACCOUNTS: 'clientAccounts',
    /** MobX ClientStore reads this key in `init()`; `is_logged_in` requires `accounts[loginid].token`. */
    CLIENT_ACCOUNTS_STORAGE: 'client.accounts',
    ACTIVE_LOGINID: 'active_loginid',
} as const;

/** Sync session active keys the same way `storeClientAccounts` does after URL login. */
const syncSessionActiveLoginKeys = (activeLoginid: string) => {
    try {
        if (/^(CR|MF|VRTC)\d/.test(activeLoginid)) {
            sessionStorage.setItem('active_loginid', activeLoginid);
        }
        if (/^(CRW|MFW|VRW)\d/.test(activeLoginid)) {
            sessionStorage.setItem('active_wallet_loginid', activeLoginid);
        }
        if (/^DOT/i.test(activeLoginid)) {
            sessionStorage.setItem('active_loginid', activeLoginid);
        }
    } catch {
        /* ignore */
    }
};

type TLegacyDetail = {
    loginid?: string;
    currency?: string;
    is_virtual?: 0 | 1 | boolean;
    account_type?: string;
    landing_company_name?: string;
    broker?: string;
};

/**
 * Build `client.accounts` object (loginid -> row with `token`) for ClientStore.init / is_logged_in.
 */
const buildClientAccountsFromLegacyPayload = (
    tokens: Record<string, string>,
    account_details: Record<string, TLegacyDetail>,
    active_loginid: string
): Record<string, Record<string, unknown>> => {
    const client_object: Record<string, Record<string, unknown>> = {};
    const loginids = Object.keys(tokens || {});
    for (const loginid of loginids) {
        const token = tokens[loginid];
        if (!token) continue;
        const d = account_details?.[loginid] || {};
        const isVirt =
            d.is_virtual === 1 || d.is_virtual === true
                ? 1
                : d.is_virtual === 0 || d.is_virtual === false
                  ? 0
                  : /^VR/i.test(loginid)
                    ? 1
                    : 0;
        client_object[loginid] = {
            loginid,
            token,
            currency: (typeof d.currency === 'string' && d.currency) || 'USD',
            is_virtual: isVirt,
            ...(d.account_type ? { account_type: d.account_type } : {}),
            ...(d.landing_company_name ? { landing_company_shortcode: d.landing_company_name } : {}),
            ...(d.broker ? { broker: d.broker } : {}),
        };
    }
    if (active_loginid && !client_object[active_loginid] && tokens[active_loginid]) {
        client_object[active_loginid] = {
            loginid: active_loginid,
            token: tokens[active_loginid],
            currency: 'USD',
            is_virtual: /^VR/i.test(active_loginid) ? 1 : 0,
        };
    }
    return client_object;
};

const buildClientAccountsFromOAuth2Payload = (
    payload: Extract<TAuthPayload, { flow: 'oauth2' }>
): Record<string, Record<string, unknown>> => {
    const client_object: Record<string, Record<string, unknown>> = {};
    const list = payload.account_list?.length ? payload.account_list : [{ account_id: payload.active_account_id, currency: 'USD' }];
    for (const acc of list) {
        const id = acc.account_id;
        if (!id) continue;
        const isVirt = acc.is_virtual === 1 ? 1 : acc.is_virtual === 0 ? 0 : /^VR/i.test(id) ? 1 : 0;
        client_object[id] = {
            loginid: id,
            token: payload.access_token,
            currency: acc.currency || 'USD',
            is_virtual: isVirt,
            ...(acc.account_type ? { account_type: acc.account_type } : {}),
        };
    }
    return client_object;
};

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Registrable PU host roots allowed to embed this app (any subdomain). Align with root cookie
 * `Domain=.<registrable>` and deploy CSP `frame-ancestors` where possible.
 */
const ALLOWED_PARENT_HOST_SUFFIXES: readonly string[] = ['botblitz.org', 'botblitz.site'];

const isLocalDevHost = (hostname: string): boolean => {
    const h = hostname.toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
};

const parseExplicitOriginsFromEnv = (): Set<string> => {
    const set = new Set<string>();
    const raw = `${process.env.PU_PARENT_ORIGIN || ''},${process.env.BOTBLITZ_PARENT_ORIGINS || ''},${process.env.EVERYMAN_PARENT_ORIGINS || ''}`;
    for (const part of raw.split(',')) {
        const t = part.trim();
        if (!t) continue;
        try {
            set.add(new URL(t).origin);
        } catch {
            /* ignore */
        }
    }
    return set;
};

let memoEnvOrigins: Set<string> | null = null;
const getExplicitEnvOrigins = (): Set<string> => {
    if (!memoEnvOrigins) memoEnvOrigins = parseExplicitOriginsFromEnv();
    return memoEnvOrigins;
};

const isTrustedParentOrigin = (origin: string): boolean => {
    if (!origin) return false;
    if (getExplicitEnvOrigins().has(origin)) return true;
    try {
        const url = new URL(origin);
        if (IS_PRODUCTION && url.protocol === 'http:' && !isLocalDevHost(url.hostname)) return false;

        const host = url.hostname.toLowerCase();

        // Non-production: allow http(s) localhost for local PU + iframe dev.
        if (!IS_PRODUCTION && isLocalDevHost(host)) return true;

        return ALLOWED_PARENT_HOST_SUFFIXES.some(suffix => host === suffix || host.endsWith(`.${suffix}`));
    } catch {
        return false;
    }
};

/** Direct parent's origin (`ancestorOrigins[0]` or `referrer`). */
const resolveDirectParentOrigin = (): string => {
    try {
        if (typeof window === 'undefined') return '';
        const ancestors = (window as unknown as { location?: { ancestorOrigins?: DOMStringList } }).location
            ?.ancestorOrigins;
        if (ancestors?.length) {
            const o = ancestors.item(0);
            if (o) return o;
        }
        if (document.referrer) return new URL(document.referrer).origin;
    } catch {
        /* ignore */
    }
    return '';
};

const summarizeAuthMessageForLog = (payload: TAuthPayload) => {
    if (payload.flow === 'oauth2') {
        return {
            flow: 'oauth2' as const,
            active_hint: logAccountHint(payload.active_account_id),
            access_token_len: typeof payload.access_token === 'string' ? payload.access_token.length : 0,
            expires_at: payload.expires_at,
            account_list_len: payload.account_list?.length ?? 0,
        };
    }
    const keys = Object.keys(payload.tokens || {});
    return {
        flow: 'legacy' as const,
        active_hint: logAccountHint(payload.active_loginid),
        token_loginids_count: keys.length,
        account_details_keys: Object.keys(payload.account_details || {}).length,
    };
};

const persistLegacyAuth = (payload: Extract<TAuthPayload, { flow: 'legacy' }>) => {
    try {
        localStorage.setItem(STORAGE_KEYS.AUTH_FLOW, 'legacy');
        // accountsList shape: { [loginid]: token }
        localStorage.setItem(STORAGE_KEYS.ACCOUNTS_LIST, JSON.stringify(payload.tokens));
        localStorage.setItem(STORAGE_KEYS.CLIENT_ACCOUNTS, JSON.stringify(payload.account_details));
        localStorage.setItem(STORAGE_KEYS.ACTIVE_LOGINID, payload.active_loginid);

        const client_accounts = buildClientAccountsFromLegacyPayload(
            payload.tokens,
            payload.account_details as Record<string, TLegacyDetail>,
            payload.active_loginid
        );
        localStorage.setItem(STORAGE_KEYS.CLIENT_ACCOUNTS_STORAGE, JSON.stringify(client_accounts));
        localStorage.setItem('client_account_details', JSON.stringify(Object.values(client_accounts)));
        syncSessionActiveLoginKeys(payload.active_loginid);

        // Clean up any stale OAuth2 keys.
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN_EXPIRES_AT);
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACTIVE_ACCOUNT_ID);
    } catch (e) {
        console.error('iframe-bridge: failed to persist legacy auth payload:', e);
    }
};

const persistOAuth2Auth = (payload: Extract<TAuthPayload, { flow: 'oauth2' }>) => {
    try {
        localStorage.setItem(STORAGE_KEYS.AUTH_FLOW, 'oauth2');
        localStorage.setItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN, payload.access_token);
        if (payload.expires_at > 0) {
            localStorage.setItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN_EXPIRES_AT, String(payload.expires_at));
        }
        localStorage.setItem(STORAGE_KEYS.OAUTH2_ACTIVE_ACCOUNT_ID, payload.active_account_id);
        localStorage.setItem(STORAGE_KEYS.ACTIVE_LOGINID, payload.active_account_id);

        const client_accounts = buildClientAccountsFromOAuth2Payload(payload);
        localStorage.setItem(STORAGE_KEYS.CLIENT_ACCOUNTS_STORAGE, JSON.stringify(client_accounts));
        localStorage.setItem(
            STORAGE_KEYS.CLIENT_ACCOUNTS,
            JSON.stringify(client_accounts)
        );
        localStorage.setItem('client_account_details', JSON.stringify(Object.values(client_accounts)));
        syncSessionActiveLoginKeys(payload.active_account_id);

        // Clean up any stale legacy token map (V2 uses Bearer on each account row for WS gate).
        localStorage.removeItem(STORAGE_KEYS.ACCOUNTS_LIST);
    } catch (e) {
        console.error('iframe-bridge: failed to persist oauth2 auth payload:', e);
    }
};

const clearLocalAuthState = () => {
    try {
        localStorage.removeItem(STORAGE_KEYS.AUTH_FLOW);
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACCESS_TOKEN_EXPIRES_AT);
        localStorage.removeItem(STORAGE_KEYS.OAUTH2_ACTIVE_ACCOUNT_ID);
        localStorage.removeItem(STORAGE_KEYS.ACCOUNTS_LIST);
        localStorage.removeItem(STORAGE_KEYS.CLIENT_ACCOUNTS);
        localStorage.removeItem(STORAGE_KEYS.CLIENT_ACCOUNTS_STORAGE);
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_LOGINID);
        localStorage.removeItem('client_account_details');
        try {
            sessionStorage.removeItem('active_loginid');
            sessionStorage.removeItem('active_wallet_loginid');
            sessionStorage.removeItem('account');
        } catch {
            /* ignore */
        }
    } catch {
        /* ignore */
    }
};

export type TBridgeRuntime = {
    onAuth?: (payload: TAuthPayload) => Promise<void> | void;
    onSwitch?: (account: string) => Promise<void> | void;
    onLogout?: () => Promise<void> | void;
};

let runtime: TBridgeRuntime = {};
let parentOrigin = '';
let started = false;

const postToParent = (message: TIframeToHostMessage) => {
    try {
        if (window.parent && parentOrigin) {
            window.parent.postMessage(message, parentOrigin);
        }
    } catch (e) {
        console.error('iframe-bridge: postMessage to parent failed:', e);
    }
};

const handleHostMessage = async (event: MessageEvent) => {
    if (!parentOrigin || event.origin !== parentOrigin) return;
    if (!isProtocolMessage(event.data)) return;

    const message = event.data as THostToIframeMessage;

    try {
        if (message.type === 'AUTH') {
            // eslint-disable-next-line no-console
            console.info(AUTH_VERBOSE, 'AUTH_received', {
                origin: event.origin,
                ...summarizeAuthMessageForLog(message.payload),
            });

            if (!validateSyncCookieAllowsPayload(message.payload)) {
                // eslint-disable-next-line no-console
                console.warn('🖼️ [everyman-dtrader-bridge] AUTH rejected: sync cookie mismatch');
                postToParent({
                    type: 'AUTH_ERROR',
                    protocol: PROTOCOL,
                    code: 'SyncCookieMismatch',
                    message: 'Auth payload does not match PU sync cookie (login id / flow).',
                });
                return;
            }
            if (message.payload.flow === 'oauth2') {
                persistOAuth2Auth(message.payload);
            } else {
                persistLegacyAuth(message.payload);
            }
            // eslint-disable-next-line no-console
            console.info(AUTH_VERBOSE, 'AUTH_persisted_to_LS', {
                flow: message.payload.flow,
                auth_flow_ls: (() => {
                    try {
                        return localStorage.getItem(STORAGE_KEYS.AUTH_FLOW);
                    } catch {
                        return null;
                    }
                })(),
                active_loginid_len: (() => {
                    try {
                        return (localStorage.getItem(STORAGE_KEYS.ACTIVE_LOGINID) || '').length;
                    } catch {
                        return 0;
                    }
                })(),
                client_accounts_keys: (() => {
                    try {
                        const raw = localStorage.getItem(STORAGE_KEYS.CLIENT_ACCOUNTS_STORAGE);
                        if (!raw) return 0;
                        const o = JSON.parse(raw) as Record<string, unknown>;
                        return Object.keys(o || {}).length;
                    } catch {
                        return -1;
                    }
                })(),
            });
            // eslint-disable-next-line no-console
            console.info('🖼️ [everyman-dtrader-bridge] AUTH_applied', {
                flow: message.payload.flow,
                loginid_len:
                    message.payload.flow === 'oauth2'
                        ? String(message.payload.active_account_id || '').length
                        : String(message.payload.active_loginid || '').length,
            });
            /*
             * Root api-base V2 order: persist Bearer → POST /otp (`requestOptionsOtpWsUrl`) → `new WebSocket(url)` → then app init.
             * Run OTP WS connect before `client.init` so `DerivAPIBasic` exists when synthetic hydrate runs (no polling).
             */
            if (message.payload.flow === 'oauth2') {
                try {
                    const already_open =
                        typeof BinarySocket.hasReadyState === 'function' && BinarySocket.hasReadyState(1);
                    if (already_open) {
                        await BinarySocket.closeAndOpenNewConnection();
                    } else {
                        await BinarySocket.openNewConnection();
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('🖼️ [everyman-dtrader-bridge] OAuth2 WS open after AUTH failed:', e);
                }
            }

            try {
                await runtime.onAuth?.(message.payload);
                // eslint-disable-next-line no-console
                console.info(AUTH_VERBOSE, 'onAuth_runtime_callback', { outcome: 'finished_no_throw' });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(AUTH_VERBOSE, 'onAuth_runtime_callback', {
                    outcome: 'threw',
                    error: e instanceof Error ? e.message : String(e),
                });
                throw e;
            }

            const authedLoginid =
                message.payload.flow === 'oauth2'
                    ? message.payload.active_account_id
                    : message.payload.active_loginid;
            postToParent({ type: 'AUTHED', protocol: PROTOCOL, loginid: authedLoginid });
        } else if (message.type === 'SWITCH') {
            localStorage.setItem(STORAGE_KEYS.ACTIVE_LOGINID, message.account);
            syncSessionActiveLoginKeys(message.account);
            await runtime.onSwitch?.(message.account);
        } else if (message.type === 'LOGOUT') {
            clearLocalAuthState();
            await runtime.onLogout?.();
        }
    } catch (e) {
        const code = e instanceof Error ? e.name : 'BridgeHandlerError';
        const msg = e instanceof Error ? e.message : 'Unknown error in iframe bridge handler';
        postToParent({ type: 'AUTH_ERROR', protocol: PROTOCOL, code, message: msg });
    }
};

/**
 * Boot the bridge. Call once from `index.tsx` before rendering the React tree. Subsequent calls are
 * no-ops. The provided `runtime` callbacks fire AFTER the bridge has hydrated localStorage so the
 * existing client-store init path can read the new state.
 */
export const startIframeBridge = (initRuntime: TBridgeRuntime = {}): void => {
    if (started) return;
    started = true;
    runtime = initRuntime;

    if (typeof window === 'undefined' || window.parent === window) {
        // Standalone load; bridge is a no-op.
        return;
    }

    const directParent = resolveDirectParentOrigin();
    if (!directParent || !isTrustedParentOrigin(directParent)) {
        console.warn(
            'iframe-bridge: direct parent origin not allowed or could not be resolved; bridge disabled.',
            { direct_parent: directParent || '(empty)' }
        );
        return;
    }

    parentOrigin = directParent;

    window.addEventListener('message', handleHostMessage);
    // eslint-disable-next-line no-console
    console.info('🖼️ [everyman-dtrader-bridge] READY_sent', { parent_origin_configured: !!parentOrigin });
    postToParent({ type: 'READY', protocol: PROTOCOL });
};

export const isBridgeActive = (): boolean => started && parentOrigin !== '';
